import { useEffect } from 'react';
import { useScanService } from '@witmetrics/data-store';
import type { ServiceSettings } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { usePath } from '@/hooks/usePath';
import { useTab } from '@/hooks/useTab';
import TabsBlock from '@/components/TabsBlock';
import Card from '@/components/Card';
import FilesTab, { type FilesTabProps } from '../tabs/FilesTab';
import SettingsTab, { type SettingsTabProps } from '../tabs/SettingsTab';
import ExportsTab from '../tabs/ExportsTab';
import type { CaseOverlayTab } from '../types';
import { classes, getDefaultTab } from './utils';

export type TabsColumnProps = {
  isFetchingData: boolean;
  openFileIds: FilesTabProps['openFileIds'];
  serviceSettings?: ServiceSettings;
  onFetchServiceSettings: SettingsTabProps['onFetchServiceSettings'];
  onToggleFileId: FilesTabProps['onToggleFileId'];
  onOpenCollection: FilesTabProps['onOpenCollection'];
  onCloseCollection: FilesTabProps['onCloseCollection'];
};

export default function TabsColumn({
  isFetchingData,
  openFileIds,
  serviceSettings,
  onFetchServiceSettings,
  onToggleFileId,
  onOpenCollection,
  onCloseCollection,
}: TabsColumnProps) {
  const { activeServiceId } = useAppState();
  const { searchParams, pushParams } = usePath();
  const scanService = useScanService(activeServiceId);
  const [defaultTab, setDefaultTab] = useTab<CaseOverlayTab>(
    getDefaultTab(searchParams)
  );

  useEffect(() => {
    const tab = searchParams.get('caseOverlayTab');
    if (tab !== null && tab !== defaultTab) {
      handleSelectTab(tab as CaseOverlayTab);
    }
  }, [searchParams]);

  const handleSelectTab = (tab: CaseOverlayTab) => {
    setDefaultTab(tab);
    pushParams({ caseOverlayTab: tab });
  };

  if (!scanService) return null;

  return (
    <Card className={classes.card}>
      <TabsBlock<CaseOverlayTab[]>
        className={classes.tabsBlock}
        name="file-info"
        bodyClassName={classes.tabBody}
        defaultTab={defaultTab}
        onChange={handleSelectTab}>
        <FilesTab
          isFetchingData={isFetchingData}
          openFileIds={openFileIds}
          name="files"
          label="Files"
          onToggleFileId={onToggleFileId}
          onOpenCollection={onOpenCollection}
          onCloseCollection={onCloseCollection}
        />
        <ExportsTab name="exports" label="Export" />
        <SettingsTab
          isFetchingData={isFetchingData}
          name="settings"
          label="Settings"
          serviceSettings={serviceSettings}
          onFetchServiceSettings={onFetchServiceSettings}
        />
      </TabsBlock>
    </Card>
  );
}
